/**
 * Display helpers for application documents (Documents tab + History).
 */

const UPLOAD_TYPES = new Set([
  'resume_document',
  'presentation_letter_document',
  'portfolio_document',
]);

const GENERATED_TYPES = new Set([
  'resume_content',
  'resume_structured',
  'presentation_letter_content',
  'report_screening',
]);

/** Compact UTC timestamp for filenames — e.g. 20260716T1541Z */
export function formatDocumentFileTimestamp(iso: string | undefined | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}Z`;
}

export function documentTypeLabel(type: string, t: (key: string) => string): string {
  switch (type) {
    case 'resume_document':
      return t('appDetail.docTypeResume');
    case 'presentation_letter_document':
      return t('appDetail.docTypePresentationLetter');
    case 'portfolio_document':
      return t('appDetail.docTypePortfolio');
    case 'resume_content':
      return t('appDetail.docTypeResumeContent');
    case 'resume_structured':
      return t('appDetail.docTypeResumeStructured');
    case 'presentation_letter_content':
      return t('appDetail.docTypePresentationLetterContent');
    case 'report_screening':
      return t('appDetail.docTypeScreeningReport');
    default:
      return type.replace(/_/g, ' ');
  }
}

function fileExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot) : '';
}

function insertTimestampBeforeExtension(fileName: string, ts: string): string {
  if (!ts) return fileName;
  const dot = fileName.lastIndexOf('.');
  if (dot === -1) return `${fileName}_${ts}`;
  return `${fileName.slice(0, dot)}_${ts}${fileName.slice(dot)}`;
}

function generatedBaseName(type: string, candidateSlug: string): string {
  switch (type) {
    case 'resume_content':
      return candidateSlug ? `resume_content_${candidateSlug}` : 'resume_content';
    case 'resume_structured':
      return candidateSlug ? `resume_structured_${candidateSlug}` : 'resume_structured';
    case 'presentation_letter_content':
      return candidateSlug
        ? `presentation_letter_content_${candidateSlug}`
        : 'presentation_letter_content';
    case 'report_screening':
      return candidateSlug ? `report_screening_${candidateSlug}` : 'report_screening';
    default:
      return type;
  }
}

export function formatApplicationDocumentDisplay(
  doc: { type: string; name: string; uploadedAt: string },
  candidateSlug: string,
  t: (key: string) => string,
): { displayName: string; typeLabel: string } {
  const typeLabel = documentTypeLabel(doc.type, t);
  const ts = formatDocumentFileTimestamp(doc.uploadedAt);
  const ext = fileExtension(doc.name) || (doc.type.includes('json') ? '.json' : doc.type.includes('content') ? '.md' : '');

  if (UPLOAD_TYPES.has(doc.type)) {
    const baseName = doc.name.trim() || `${typeLabel.replace(/\s+/g, '_')}${ext}`;
    return {
      displayName: insertTimestampBeforeExtension(baseName, ts),
      typeLabel,
    };
  }

  if (GENERATED_TYPES.has(doc.type)) {
    const base = generatedBaseName(doc.type, candidateSlug);
    return {
      displayName: ts ? `${base}_${ts}${ext || (doc.type === 'resume_structured' || doc.type === 'report_screening' ? '.json' : '.md')}` : `${base}${ext}`,
      typeLabel,
    };
  }

  return {
    displayName: ts ? insertTimestampBeforeExtension(doc.name, ts) : doc.name,
    typeLabel,
  };
}

export function buildScreeningActivityDetail(
  rawDocuments: { type: string; name: string }[] | null | undefined,
  t: (key: string, params?: Record<string, string>) => string,
): string | undefined {
  if (!rawDocuments?.length) return undefined;

  const uploaded: string[] = [];
  if (rawDocuments.some((d) => d.type === 'resume_document')) {
    uploaded.push(t('appDetail.docTypeResume'));
  }
  if (rawDocuments.some((d) => d.type === 'presentation_letter_document')) {
    uploaded.push(t('appDetail.docTypePresentationLetter'));
  }
  if (rawDocuments.some((d) => d.type === 'portfolio_document')) {
    uploaded.push(t('appDetail.docTypePortfolio'));
  }

  const generated: string[] = [];
  if (rawDocuments.some((d) => d.type === 'resume_structured')) {
    generated.push(t('appDetail.docTypeResumeStructured'));
  }
  if (rawDocuments.some((d) => d.type === 'resume_content')) {
    generated.push(t('appDetail.docTypeResumeContent'));
  }
  if (rawDocuments.some((d) => d.type === 'presentation_letter_content')) {
    generated.push(t('appDetail.docTypePresentationLetterContent'));
  }

  const parts: string[] = [];
  if (uploaded.length) {
    parts.push(t('appDetail.historyDetailUploaded', { docs: uploaded.join(', ') }));
  }
  if (generated.length) {
    parts.push(t('appDetail.historyDetailGenerated', { docs: generated.join(', ') }));
  }
  return parts.length ? parts.join(' · ') : undefined;
}

export function buildGenerateReportActivityDetail(
  reports: { generatedAt: string; basedOnDocuments: { label: string }[]; overallScore?: number }[],
  activitySeconds: number,
  t: (key: string, params?: Record<string, string | number>) => string,
): string | undefined {
  if (!reports.length) return undefined;

  const matched = reports.reduce<(typeof reports)[0] | null>((best, r) => {
    const sec = new Date(r.generatedAt).getTime() / 1000;
    if (!best) return r;
    const bestSec = new Date(best.generatedAt).getTime() / 1000;
    return Math.abs(sec - activitySeconds) < Math.abs(bestSec - activitySeconds) ? r : best;
  }, null);

  if (!matched?.basedOnDocuments?.length) return undefined;

  const labels = matched.basedOnDocuments.map((d) => d.label).join(', ');
  const scorePart =
    typeof matched.overallScore === 'number'
      ? ` · ${t('appDetail.historyDetailScore', { score: matched.overallScore })}`
      : '';
  return `${t('appDetail.historyDetailBasedOn', { docs: labels })}${scorePart}`;
}
